import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const SPINNER_SIZES = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
  xl: 'h-8 w-8',
} as const;

export type SpinnerSize = keyof typeof SPINNER_SIZES;

interface SpinnerProps {
  /** Icon size. Default 'md' (h-5). */
  size?: SpinnerSize;
  /** Optional text shown beside the spinner. */
  label?: string;
  /** Center the spinner in a padded flex box, for section / page loading. */
  center?: boolean;
  /** Color / spacing overrides. Colour inherits from the parent (`currentColor`); pass e.g. `text-gray-400` for a section loader. */
  className?: string;
}

/**
 * The one canonical loading spinner (#5342). Wraps `Loader2` with standard sizes and a muted default
 * colour, replacing the scattered inline `<Loader2 className="... animate-spin" />` variants (which
 * used inconsistent `h-4`/`h-5`/`h-8` sizes and gray/blue colours). Colour is inherited from the
 * parent (`currentColor`) so it stays correct inside coloured buttons; pass a text colour via
 * `className` for section loaders (e.g. `text-gray-400`).
 */
export function Spinner({ size = 'md', label, center = false, className }: SpinnerProps) {
  // Bare Loader2 in the common case so this is a true drop-in for an inline `<Loader2 className=... />`
  // (e.g. inside a button, where the caller's `mr-2` must sit directly between icon and label). Only
  // wrap when a label or centering is actually requested.
  const loader = (
    <Loader2 className={cn(SPINNER_SIZES[size], 'animate-spin', className)} role="status" aria-label="Loading" />
  );
  if (label) {
    return (
      <span className="inline-flex items-center gap-2">
        {loader}
        <span className="text-sm text-gray-500">{label}</span>
      </span>
    );
  }
  return center ? <div className="flex items-center justify-center p-8">{loader}</div> : loader;
}
